import { Component, NgZone, ChangeDetectorRef, ChangeDetectionStrategy, ElementRef, ViewEncapsulation, Input } from '@angular/core';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { EnpsControl } from '@common/controls/enps-control/enps-control';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { AppService } from '@suite/app.service';
import { TimeZone } from '@common/data-models/complex-types/time-zone';
import { GlobalTablesService } from "@common/services/global-tables-service";
import { VisualStateService } from '@common/services/visual-state-service';
import { LanguageSettings } from '@common/services/language-settings';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import moment, { Moment } from 'moment-timezone';
import { FormControl, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';

@Component({
    selector: 'date-time-picker',
    templateUrl: './date-time-picker.html',
    // changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./date-time-picker.scss'],
    host: {
        class: 'st-date-time-picker'

    },
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    ]
})

export class DateTimePicker extends EnpsControl<DateTime> {

    public clientTimeZone: TimeZone;
    private _min: DateTime = new DateTime();

    @Input()
    get min(): DateTime {
        return this._min;
    }
    set min(value: DateTime) {
        if (value) {
            this._min = value;
        }
    }

    get minDate(): Moment {
        return this.min?.moment || null;
    }

    public commonTimeZones: Array<TimeZone> = [];
    public ianaTimeZones: Array<TimeZone> = [];
    public currentTimeZone: TimeZone;
    public zones: Array<TimeZone> = [];
    internalFormControl: FormControl<Moment> = new FormControl(null);

    private OSTimeZone: TimeZone;

    constructor(
        public appService: AppService,
        ngZone: NgZone,
        changeDetectorRef: ChangeDetectorRef,
        timeoutQueue: TimeoutQueue,
        globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public languageSettings: LanguageSettings,
        elmRef: ElementRef,
        private dateAdapter: DateAdapter<Moment>,
    ) {
        super(appService, ngZone, changeDetectorRef, timeoutQueue, globalTablesService, elmRef);
        dateAdapter.setLocale(this.locale);
    }

    get showTimeZone() {
        return !this.asCustomField;
    }

    async ngOnInit() {
        this.commonTimeZones = this.globalTablesService.CommonTimeZones;
        this.ianaTimeZones = this.globalTablesService.IanaTimeZones;
        this.clientTimeZone = this.globalTablesService.ClientTimeZone;
        this.currentTimeZone = this.globalTablesService.PersonalTimeZone ? this.globalTablesService.PersonalTimeZone : this.globalTablesService.ClientTimeZone;
        this.OSTimeZone = this.globalTablesService.ClientTimeZone;
    }

    dateChange(value: MatDatepickerInputEvent<Moment>) {
        if (value.value) {
            let newDate = this.value.get();
            if(!newDate) {
                newDate = new Date();
            }
            newDate?.setFullYear(value.value.toDate().getFullYear());
            newDate?.setMonth(value.value.toDate().getMonth(), value.value.toDate().getDate());
            const newDateTime = new DateTime(newDate);
            this.changed.emit(newDateTime);
        } else {
            this.value.set(null);
            this.changed.emit(this.value);
        }
    }

    public timeChanged(value: Moment) {
        if (value) {
            const currentDate = this.value?.get() || new Date();
            const newDate = DateTime.convertToTargetTZ(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds()), this.clientTimeZone.id, this.currentTimeZone.id)

            const newTime = value.toDate();
            newDate.setHours(newTime.getHours());
            newDate.setMinutes(newTime.getMinutes());
            newDate.setSeconds(newTime.getSeconds());
            const newDateTime = new DateTime(newDate);
            this.changed.emit(newDateTime);
        }
    }

    public clearDate() {
        this.value.set(null);
        this.internalFormControl.setValue(null);
        this.changed.emit(this.value);
    }

    public getDatePicker(picker: any) {
        setTimeout(() => {
            picker._componentRef?.instance?._calendar.monthView.selectedChange.pipe(take(1)).subscribe((res: Moment) => {
                let newDate = this.value.get();
                if(!newDate) {
                    newDate = new Date();
                }
                newDate?.setFullYear(res.toDate().getFullYear());
                newDate?.setMonth(res.toDate().getMonth(), res.toDate().getDate());
                const newDateTime = new DateTime(newDate);
                this.changed.emit(newDateTime);
                picker.close()
            })
        }, 0)
    }

    get dateValue(): Moment {
        if (this.value?.get()?.getFullYear() == 1800) {
            return null;
        }
        return this.value.moment;
    }

    public timezoneChanged(event: string) {
        this.currentTimeZone = [...this.commonTimeZones, ...this.ianaTimeZones].find(tz => tz.id == event);
        const currentDate = this.value.get() || new Date();
        const newDate = DateTime.convertToTargetTZ(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds()), this.clientTimeZone.id, this.currentTimeZone.id)
        const newDateTime = new DateTime();
        newDateTime.set(newDate)
        this.changed.emit(newDateTime);
    }

    private getClientTimeZone(localtimezone: string) {
        let tmpArr = [...this.commonTimeZones, ...this.ianaTimeZones];
        let tzArr = tmpArr.filter((item) => {
            return item.id == localtimezone;
        })

        if (tzArr.length > 0) {
            return tzArr[0];
        }

        return this.globalTablesService.DefaultTimeZone;
    }

    public getTimeZone() {
        let localtimezone = '';
        try {
            localtimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (err) {
        }

        this.globalTablesService.ClientTimeZone = this.getClientTimeZone(localtimezone);
        if (this.globalTablesService.PersonalTimeZone) {
            this.OSTimeZone = this.globalTablesService.PersonalTimeZone;
        } else {
            this.OSTimeZone = this.globalTablesService.ClientTimeZone;
        }
        return this.OSTimeZone.id;
    }

    _syncValueIfNecessary() {
        this.internalFormControl.setValue(this.dateValue);
    }

    _addValidatorIfNecessary() {
        this.internalFormControl.addValidators(Validators.required);
        this.internalFormControl.markAllAsTouched();
    }

    _setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.internalFormControl.disable();
        } else {
            this.internalFormControl.enable();
        }
    }
}
